// React
import React, { useEffect, useMemo, useRef, useState } from 'react';

// Refine
import { useTranslation } from '@refinedev/core';

// Mantine
import {
  Autocomplete,
  AutocompleteProps,
  Group,
  Highlight,
  Loader,
  OptionsFilter,
  Text,
  useComputedColorScheme,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';

// API
import { useSearchDiagnosis } from 'api/resource';

// Interfaces
import { Diagnosis } from 'interfaces';

// Sentry
import * as Sentry from '@sentry/react';

/**
 * Props for the `CustomReportDiagnosisSearch` component.
 * @interface CustomReportDiagnosisSearchProps
 * @property {string} [defaultValue] - The default text for the search input field
 * @property {(diagnosis: Diagnosis) => void} onChange - Callback with the selected Diagnosis object
 */
interface CustomReportDiagnosisSearchProps {
  defaultValue?: string;
  onChange: (diagnosis: Diagnosis) => void;
}

/**
 * CustomReportDiagnosisSearch — a standalone diagnosis autocomplete for reports.
 * @category Component
 * @param {CustomReportDiagnosisSearchProps} props - Component props (see interface above for more information)
 * Differences from DiagnosisSearch:
 * - Does not rely on Tariff prop
 * - Does not use a (index) prop
 * - Emits the full Diagnosis object via `onChange`
 *
 * @example
 * <CustomReportDiagnosisSearch onChange={handleChange} />
 */
export const CustomReportDiagnosisSearch: React.FC<
  CustomReportDiagnosisSearchProps
> = ({ defaultValue = '', onChange }) => {
  const { translate } = useTranslation();
  const [searchValue, setSearchValue] = useState<string>(defaultValue);
  const [debounced] = useDebouncedValue<string>(searchValue.trim(), 500);

  const computedColorScheme = useComputedColorScheme('dark');
  const color = computedColorScheme === 'dark' ? 'white' : 'black';
  const ref = useRef<HTMLInputElement>(null);

  const {
    data: diagnoses = [],
    isLoading,
    isFetching,
    refetch,
  } = useSearchDiagnosis({ searchValue: debounced });

  useEffect(() => {
    refetch();
  }, [debounced]);

  const showNoDiagnosisMessage =
    debounced.length > 0 && !isLoading && !isFetching && diagnoses.length === 0;

  const options = useMemo(() => {
    if (showNoDiagnosisMessage) {
      const msg = translate('components.diagnosisSearch.noDiagnosesFound');
      return [{ value: '__NO_RESULTS__', label: msg, disabled: true }];
    }

    return diagnoses.map((d) => ({
      value: JSON.stringify(d),
      label: `${d.icd9} - ${d.description ?? ''}`,
    }));
  }, [showNoDiagnosisMessage, diagnoses, translate]);

  const renderAutocompleteOption: AutocompleteProps['renderOption'] = ({
    option,
  }: {
    option: { value: string; label: string };
  }) => {
    if (!option) return <Loader />;

    if (option.value === '__NO_RESULTS__') {
      return (
        <Text c="dimmed" size="sm">
          {option.label}
        </Text>
      );
    }

    let value: Diagnosis | null = null;
    try {
      value = JSON.parse(option.value);
    } catch (error) {
      Sentry.captureException(error);
    }

    if (!value) return <Text size="sm">{translate('noResults')}</Text>;

    return (
      <Group gap="xs">
        <div>
          <Text size="sm">
            <Highlight highlight={searchValue}>
              {value.icd9}
            </Highlight>
          </Text>
          <Group gap="xs">
            <Text size="xs" opacity={0.5}>
              {translate('description')}
            </Text>
            <Highlight size="xs" highlight={searchValue}>
              {value.description ?? ''}
            </Highlight>
          </Group>
        </div>
      </Group>
    );
  };

  const optionsFilter: OptionsFilter = ({ options }) => options;

  return (
    <Autocomplete
      ref={ref}
      comboboxProps={{ withinPortal: true }}
      defaultValue={defaultValue}
      placeholder={translate('components.diagnosisSearch.placeholder')}
      style={{ width: '90%' }}
      radius="md"
      size="sm"
      data-testid="custom-report-diagnosis-search-autocomplete"
      c={color}
      data={options}
      renderOption={renderAutocompleteOption}
      // Select all text, on focus and mouse down for quicker re-searching
      onFocus={() => {
        const el = ref.current;
        if (el) {
          // Defer to ensure Mantine/internal focus completes before selection
          requestAnimationFrame(() => {
            el.select();
          });
        }
      }}
      onMouseDown={(e) => {
        const el = ref.current;
        if (!el) return;
        // Prevent caret placement so click selects all text reliably
        e.preventDefault();
        el.focus();
        // Ensure focus is applied before selection
        setTimeout(() => el.select(), 0);
      }}
      onChange={(value) => setSearchValue(value)}
      onOptionSubmit={(value) => {
        if (value === '__NO_RESULTS__') return;
        try {
          const d: Diagnosis = JSON.parse(value);
          onChange(d);
        } catch (error) {
          Sentry.captureException(error);
        }
      }}
      filter={optionsFilter}
      rightSection={
        (isLoading || isFetching) && (
          <Loader
            style={{ position: 'absolute', right: 15, top: 10 }}
            size={20}
            color={color}
          />
        )
      }
    />
  );
};
